import Big from 'big.js';
import { AxiosStatusCode } from 'smg/axios';
import { Start, Stop } from 'smg/buttons';
import * as models from 'smg/customer-api/models';
import * as smgPromo from 'smg/customer-api/promos';
import { ShowError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import * as html from 'smg/html';
import { DollarFormat } from 'smg/util';

import * as carts from '../carts';
import { DrawCartItemPreviewFromCart } from '../components/cart-item-preview';
import { DrawPrepaymentDeposits } from '../components/prepayment-deposits';
import { Relref } from '../hugo';
import i18n from '../i18n';

import { EntryLabel, OrderPaid, PaymentCommitted, PaymentPlan, SplitCharging, SubmitRunning } from './payment/plan';
import { ChargingNotice, OnTotalMaybeChanged, SplitBanner } from './payment/split';
import { UpdateSmartPayWarning } from './smartpay';
import { State } from './state';

// SetupSummary wires the order-summary side panel: totals, promo code,
// cart preview, and the "authorize payment" button text. Exposes
// updateTotals / updateOrderSummary via state so any section can trigger
// a refresh after it mutates selections.
export function SetupSummary(state: State): void {
    state.syncSplitSummary = () => SyncSplitSummary(state);
    state.updateTotals = () => updateTotals(state);
    state.updateOrderSummary = () => updateOrderSummary(state);

    setupPromoInput(state);
    setupPromoPlaceholder(state);
}

// DrawInitialCart fetches the cart once on page load to render the preview
// column. Bounces the user to /cart if their cart is missing or empty.
// Returns the cart response so resume can seed from resumableOrder without
// a second GetCart (#7956/#8117).
export async function DrawInitialCart(state: State): Promise<models.CartSummaryResponse | undefined> {
    return await carts.GetCart().then(cartResp => {
        if (!cartResp?.cart) {
            return undefined;
        }
        if (!cartResp?.cart?.cartItems?.length) {
            window.location.href = Relref('/cart');
        }
        DrawCartItemPreviewFromCart(state.dom.$cartItemPreview, cartResp.cart);
        return cartResp;
    }).catch(err => {
        if (AxiosStatusCode(err) === 404) {
            window.location.href = Relref('/cart');
        } else {
            ShowError(err);
        }
        throw err;
    });
}

async function updateTotals(state: State): Promise<void> {
    // The cart is deleted once the order is paid, and the totals no longer change. A refresh must not send the customer to the empty cart.
    if (state.phase === 'finishing' || OrderPaid(state)) {
        return;
    }
    let taxAddr: carts.TaxAddress | undefined;
    if (state.shippingOption) {
        const shippingAddress = state.shippingOption.address;
        taxAddr = {
            addressLine1: shippingAddress.addressLine1 || '',
            addressLine2: shippingAddress.addressLine2,
            city: shippingAddress.city || '',
            stateID: shippingAddress.stateID || shippingAddress.state?.id,
            zip: shippingAddress.postalCode || '',
            countryID: shippingAddress.countryID || shippingAddress.country?.id || 0,
        };
    }

    if (!carts.GetCartID()) {
        window.location.href = Relref('/cart');
        return;
    }

    const requestedResuming = Boolean(state.orderResp);
    await carts.GetCart(taxAddr, state.promo?.code, { resuming: requestedResuming }).catch(err => {
        // The cart is gone once the order is paid, so a missing cart stays quiet.
        if (state.phase === 'finishing' || OrderPaid(state)) {
            return undefined;
        }
        if (AxiosStatusCode(err) === 404) {
            window.location.href = Relref('/cart');
        } else {
            ShowError(err);
        }
        throw err;
    }).then(cartResp => {
        // A response requested before payment must not repaint totals after it.
        if (state.phase === 'finishing' || OrderPaid(state)) {
            return;
        }
        // Start Over / resume can flip whether an order is being paid while a
        // request is in flight, and the stale response would repaint the wrong
        // order's discount.
        if (Boolean(state.orderResp) !== requestedResuming) {
            return;
        }
        if (!cartResp) {
            return;
        }

        state.cartID = cartResp.cart?.id;
        state.cartItems = cartResp.cart?.cartItems || [];

        state.promoDiscountAmount = Big(cartResp.discount);
        state.availableDiscountAmount = Big(cartResp.availableDiscount || 0);
        state.shippingAmount = Big(cartResp.shipping);
        state.taxAmount = Big(cartResp.taxAmount);
        state.subtotalAmount = Big(cartResp.subtotal);
        state.totalAmount = Big(cartResp.total);
        state.taxRate = Big(cartResp.taxRate);
        state.prepaymentAmount = Big(cartResp.prepayment || 0);
        state.pendingDepositOrders = cartResp.pendingDepositOrders || [];

        if (isTaxExemptForShipping(state)) {
            state.taxAmount = Big(0);
            state.taxRate = Big(0);
            // The server capped the prepayment credit against its taxed
            // pre-total; zeroing the tax client-side can shrink what the cart
            // owes below that cap, so rebuild the total from the exempt
            // components with the credit re-capped (appliedPrepayment) so the
            // rows always add up to the displayed total.
            state.totalAmount = state.subtotalAmount.
                add(state.promoDiscountAmount).
                add(state.availableDiscountAmount).
                add(state.shippingAmount).
                sub(appliedPrepayment(state));
        }
        if (state.resumeAuthoritativeTotal) {
            state.totalAmount = state.resumeAuthoritativeTotal;
        }
        OnTotalMaybeChanged(state);

        applyAuthorizeText(state);
        UpdateSmartPayWarning(state);
    });
}

async function updateOrderSummary(state: State): Promise<void> {
    const { dom } = state;
    const shippingAddress = state.shippingOption?.address;
    await updateTotals(state).then(() => {
        if (state.promo) {
            dom.$savingsLine.classList.remove('d-none');
            dom.$savingsAmt.textContent = DollarFormat(state.promoDiscountAmount);
            dom.$promoName.textContent = state.promo.code;
        } else {
            dom.$savingsLine.classList.add('d-none');
            dom.$savingsAmt.textContent = '';
            dom.$promoName.textContent = '';
        }
        dom.$availableDiscountAmt.textContent = DollarFormat(state.availableDiscountAmount);
        dom.$availableDiscountLine.classList.toggle('d-none', state.availableDiscountAmount.eq(0));
        if (state.shippingOption) {
            if (state.shippingAmount.eq(0) && shippingAddress?.countryID === models.CountryID.COUNTRIES_UNITED_STATES) {
                dom.$shippingAmt.textContent = 'Free for the USA';
            } else {
                dom.$shippingAmt.textContent = DollarFormat(state.shippingAmount);
            }
            if (isTaxExemptForShipping(state)) {
                dom.$taxAmt.textContent = 'Exempt';
            } else {
                dom.$cartTaxRateParents.forEach(el => el.classList.remove('d-none'));
                dom.$cartTaxRateEl.forEach(el => {
                    el.textContent = state.taxRate.mul(100).toFixed(2);
                });
                dom.$taxAmt.textContent = DollarFormat(state.taxAmount);
            }
            dom.$subtotalAmt.textContent = DollarFormat(state.subtotalAmount);
            dom.$totalAmt.textContent = DollarFormat(state.totalAmount);
        } else {
            dom.$shippingAmt.textContent = 'Calculated at Checkout';
            dom.$taxAmt.textContent = 'Calculated at Checkout';
            dom.$subtotalAmt.textContent = 'Calculated at Checkout';
            dom.$totalAmt.textContent = DollarFormat(state.totalAmount);
        }

        // Display as -$X.XX so the row reads as a credit. The cap moves with
        // shipping/tax/exemption changes, so it's repainted on every refresh.
        const prepayment = appliedPrepayment(state);
        if (prepayment.gt(0)) {
            dom.$prepaymentAmt.textContent = DollarFormat(Big(0).sub(prepayment));
            dom.$prepaymentLine.classList.remove('d-none');
        } else {
            dom.$prepaymentLine.classList.add('d-none');
        }
        DrawPrepaymentDeposits(dom.$prepaymentDeposits, state.pendingDepositOrders);
        SyncSplitSummary(state);
    });
}

// The cart response's prepayment is already server-capped against its taxed
// pre-total. A tax exemption zeroes the tax client-side only, so the same cap
// could let the credit exceed what the cart actually owes — re-cap against
// the exempt pre-total so the row and the order total always agree.
function appliedPrepayment(state: State): Big {
    const preTotal = state.subtotalAmount.
        add(state.promoDiscountAmount).
        add(state.availableDiscountAmount).
        add(state.shippingAmount).
        add(state.taxAmount);
    if (preTotal.lt(0)) {
        return Big(0);
    }
    return state.prepaymentAmount.gt(preTotal) ? preTotal : state.prepaymentAmount;
}

// An exemption applies when it has no state (blanket exemption) or its state
// matches the shipping state.
function isTaxExemptForShipping(state: State): boolean {
    const ute = state.taxExemption;
    if (!ute) {
        return false;
    }
    if (typeof ute.stateID === 'undefined') {
        return true;
    }
    const shippingStateID = state.shippingOption?.address?.stateID;
    return Boolean(ute.stateID && shippingStateID && ute.stateID === shippingStateID);
}

// applyAuthorizeText shows whichever "authorize payment" label matches the
// currently-selected payment mix: PayPal, non-payment (check/wire/PO), or
// the regular card/bank charge.
function applyAuthorizeText(state: State): void {
    const { dom } = state;
    const splitLabel = document.querySelector('#authorize-payment-text-split');
    [dom.$authorizePaymentText, dom.$authorizePaymentTextPlaceOrder, dom.$authorizePaymentTextPayPal].
        forEach($ => $.classList.add('d-none'));
    splitLabel?.classList.add('d-none');

    if (state.split && state.orderResp) {
        if (splitLabel) {
            splitLabel.textContent = i18n('checkoutSplitRetry', { left: DollarFormat(PaymentPlan(state).left) });
            splitLabel.classList.remove('d-none');
        }
        return;
    }

    switch (state.payment.paymentOptionTypeID) {
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL:
        dom.$authorizePaymentTextPayPal.classList.remove('d-none');
        break;
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK:
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE:
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO:
        dom.$authorizePaymentTextPlaceOrder.classList.remove('d-none');
        break;
    default:
        dom.$authorizePaymentText.classList.remove('d-none');
        break;
    }
}

// SyncSplitSummary paints the order-summary split list, the paid balance,
// the notice, and whether Place Order can run.
export function SyncSplitSummary(state: State): void {
    const summary = state.dom.$orderSummary;
    if (!summary) {
        return;
    }
    const plan = PaymentPlan(state);
    const list = summary.querySelector('#split-payment-summary');
    const balance = summary.querySelector('#split-payment-balance');
    const notice = summary.querySelector('#checkout-summary-notice');
    const home = summary.querySelector('#checkout-summary-actions');
    const balanceActions = summary.querySelector('#split-payment-balance-actions');
    const promo = document.querySelector('#checkout-promo');
    promo?.classList.toggle('d-none', Boolean(state.orderResp));
    // Once money may have moved on this order, it cannot be started over.
    if (PaymentCommitted(state)) {
        document.querySelector('#resume-start-over')?.classList.add('d-none');
    }

    const showList = Boolean(state.split) && plan.paid.eq(0);
    const showBalance = Boolean(state.split) && plan.paid.gt(0);
    list?.classList.toggle('d-none', !showList);
    balance?.classList.toggle('d-none', !showBalance);
    balance?.classList.toggle('d-flex', showBalance);
    if (showBalance && balanceActions && notice) {
        balanceActions.append(notice, state.dom.$authorizePaymentBtn);
    } else if (home && notice) {
        home.prepend(notice);
        if (!home.contains(state.dom.$authorizePaymentBtn)) {
            home.append(state.dom.$authorizePaymentBtn);
        }
    }

    const title = summary.querySelector('#split-payment-summary-title');
    if (title) {
        title.textContent = i18n('checkoutSplitSummaryTitle', { count: String(plan.entries.length) });
    }
    const lines = summary.querySelector('#split-payment-summary-lines');
    if (lines) {
        lines.replaceChildren();
        if (state.split) {
            for (const entry of plan.entries) {
                lines.appendChild(summaryLine(entry.status, EntryLabel(entry.payment), DollarFormat(entry.amount)));
            }
        }
    }
    const paidAmt = summary.querySelector('#split-payment-paid-amount');
    const leftAmt = summary.querySelector('#split-payment-left-amount');
    if (paidAmt) {
        paidAmt.textContent = DollarFormat(plan.paid);
    }
    if (leftAmt) {
        leftAmt.textContent = DollarFormat(plan.left);
    }

    let noticeText = '';
    let spinning = false;
    if (SplitCharging(state)) {
        noticeText = ChargingNotice(state);
        spinning = true;
    } else if (state.split && SplitBanner(state.split) === 'total') {
        noticeText = i18n('checkoutSplitTotalChanged', { total: DollarFormat(plan.cover) });
    } else if (state.split && !plan.balanced) {
        noticeText = i18n('checkoutSplitNotReady');
    }
    notice?.classList.toggle('d-none', noticeText === '');
    notice?.classList.toggle('d-flex', noticeText !== '');
    notice?.querySelector('.checkout-summary-notice-spinner')?.classList.toggle('d-none', !spinning);
    notice?.querySelector('.checkout-summary-notice-icon')?.classList.toggle('d-none', spinning || noticeText === '');
    const noticeCopy = notice?.querySelector('.checkout-summary-notice-text');
    if (noticeCopy) {
        noticeCopy.textContent = noticeText;
    }

    applyAuthorizeText(state);
    if (SubmitRunning(state)) {
        state.dom.$authorizePaymentBtn.disabled = true;
        return;
    }
    const bodyOpen = Boolean(state.dom.$paymentBody?.classList.contains('show'));
    const retryInline = Boolean(state.split) && Boolean(state.orderResp) && bodyOpen && plan.balanced;
    const payReady = (state.paymentStepComplete || retryInline) && (!state.split || plan.balanced);
    const allowed = payReady && (state.detailsComplete || Boolean(state.orderResp));
    state.dom.$authorizePaymentBtn.disabled = !allowed || SplitCharging(state);
}

function summaryLine(status: string, labelText: string, amount: string): HTMLElement {
    const row = document.createElement('div');
    row.className = 'pricing-line';
    const label = document.createElement('span');
    label.className = 'd-inline-flex align-items-center gap-1';
    if (status === 'paid' || status === 'declined') {
        const mark = document.createElement('span');
        mark.className = status === 'paid' ? 'material-symbols-rounded fs-5 text-cyan' : 'material-symbols-rounded fs-5';
        mark.textContent = status === 'paid' ? 'check_circle' : 'error';
        label.appendChild(mark);
    }
    const text = document.createElement('span');
    text.textContent = labelText;
    label.appendChild(text);
    const spacer = document.createElement('span');
    spacer.className = 'pricing-spacer';
    const figure = document.createElement('span');
    figure.textContent = amount;
    row.append(label, spacer, figure);
    return row;
}

function setupPromoInput(state: State): void {
    const { dom } = state;
    state.promoInput = new smgPromo.Input(dom.$promoInput, {
        success: p => {
            state.promo = p;
            dom.$savingsLine.classList.remove('d-none');
            dom.$savingsAmt.textContent = DollarFormat(state.promoDiscountAmount);
            dom.$promoName.textContent = p.code;
            dom.$promoPreview.innerHTML = `<b>${html.Escape(p.code)}:</b> ${html.Escape(p.description)}`;
            dom.$promoPreview.classList.remove('d-none');
            dom.$collapseLink.textContent = 'Change Promo Code';
            void state.updateOrderSummary();
        },
        error: () => {
            state.promo = undefined;
            dom.$savingsLine.classList.add('d-none');
            dom.$savingsAmt.textContent = '';
            dom.$promoName.textContent = '';
            dom.$promoPreview.classList.add('d-none');
            dom.$promoPreview.innerHTML = '';
            dom.$collapseLink.textContent = 'Have a Promo Code?';
            void state.updateOrderSummary();
        },
    });

    dom.$promoApplyBtn.addEventListener('click', async () => {
        state.promoInput.dirty = true;
        Start(dom.$promoApplyBtn);
        await state.promoInput.check().
            then(() => ReportValidity(dom.$promoInput)).
            then(() => Stop(dom.$promoApplyBtn)).
            catch(() => Stop(dom.$promoApplyBtn));
    });

    // Re-check on load so a promo in the querystring takes effect and its
    // success/error callback fires.
    setTimeout(async () => {
        state.promoInput.dirty = true;
        await state.promoInput.check();
    });
}

function setupPromoPlaceholder(state: State): void {
    const update = () => updatePromoPlaceholder(state);
    update();
    window.addEventListener('resize', update);
}

function updatePromoPlaceholder(state: State): void {
    state.dom.$promoInput.placeholder = window.innerWidth > 400 ?
        'Enter promo code here...' :
        'Promo code...';
}
